import { type ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import { X, type LucideIcon } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Drawer, DrawerClose, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';

import { useContentHeight } from './useContentHeight';

export interface TabbedModalTab {
  value: string;
  label: string;
  icon: LucideIcon;
}

interface TabbedModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The accessible name. Visually hidden: the tab strip is the header. */
  title: string;
  tabs: readonly TabbedModalTab[];
  value: string;
  onValueChange: (value: string) => void;
  /** data-testid of the sub-lg bottom sheet. */
  sheetTestId: string;
  /** The `TabsContent` panels, one per tab. */
  children: ReactNode;
  /** Where focus goes on close. By default it returns to whatever had focus
   *  as the modal opened; `preventDefault()` to send it elsewhere. */
  onCloseAutoFocus?: (event: Event) => void;
  /** False while the modal must stay up (a send in flight): its close X is
   *  disabled, Escape and a click outside do nothing, and the bottom sheet
   *  cannot be swiped away. Buttons inside a panel are the caller's. */
  dismissible?: boolean;
}

// Underline tab style matching the Photos/Zillow tabs on the parent page
// (PropertyAssetManagementPage): transparent bg, no shadow, foreground border
// on active. Kept inline to avoid a one-off variant on the Tabs primitive.
const tabTriggerClass =
  'rounded-none bg-transparent shadow-none px-0 h-12 gap-2 text-base font-medium border-b-2 border-transparent text-muted-foreground transition-colors hover:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-primary data-[state=active]:text-primary';

/**
 * The photos page's tabbed modal: Sync (Sync, Webhooks) and Share (Internal
 * Share, PlanOtour). A 672px dialog at lg+ and a bottom sheet below it
 * (design 2026-08-19 round 4, spec §1.11), with the same tabbed body in both
 * so tab wiring and content cannot drift between them. The body's height
 * follows the active tab with a short transition (Share design, Header
 * Actions 2b).
 */
const TabbedModal = ({
  open,
  onOpenChange,
  title,
  tabs,
  value,
  onValueChange,
  sheetTestId,
  children,
  onCloseAutoFocus,
  dismissible = true
}: TabbedModalProps) => {
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);
  const [measureContent, contentHeight] = useContentHeight();

  // A modal Radix Dialog returns focus on close only to its Dialog.Trigger.
  // These modals are opened from outside, with no Trigger, so focus would land
  // on <body>. Remember what had focus as the modal opened (a layout effect
  // runs before Radix moves focus inside) and return it there.
  const openerRef = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (!open) return;
    openerRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, [open]);
  const handleCloseAutoFocus = (event: Event) => {
    onCloseAutoFocus?.(event);
    if (event.defaultPrevented) return;
    event.preventDefault();
    if (openerRef.current?.isConnected) openerRef.current.focus();
  };

  // A tab switched from inside a panel (a link in its copy) unmounts the
  // control that had focus along with that panel, and focus would fall out of
  // the tabs. Hand it to the tab now showing, decided by where focus is rather
  // than when Radix removes the old panel: a switch from the tab strip leaves
  // focus on a trigger, and is left alone.
  const triggerRefs = useRef(new Map<string, HTMLButtonElement>());
  const shownTab = useRef(value);
  useEffect(() => {
    if (shownTab.current === value) return;
    shownTab.current = value;
    const triggers = [...triggerRefs.current.values()];
    if (triggers.some((trigger) => trigger === document.activeElement)) return;
    triggerRefs.current.get(value)?.focus();
  }, [value]);

  // The drawer renders its own close X inside the tab strip (the Dialog's
  // built-in X occupies that spot at lg+; a Drawer has only its drag handle).
  const tabbedBody = (withDrawerClose: boolean) => (
    <Tabs value={value} onValueChange={onValueChange}>
      {/* Header: tabs left, close X right. At lg+ the Dialog's built-in
          absolute close button fills the right slot (`pr-14` reserves its
          room); the drawer branch renders an inline DrawerClose instead. */}
      <div
        className={
          withDrawerClose
            ? 'flex items-center border-b border-border pl-4 pr-2'
            : 'border-b border-border pl-6 pr-14'
        }
      >
        <TabsList className="bg-transparent rounded-none p-0 h-auto gap-6">
          {tabs.map(({ value: tabValue, label, icon: Icon }) => (
            <TabsTrigger
              key={tabValue}
              ref={(node) => {
                if (node) triggerRefs.current.set(tabValue, node);
                else triggerRefs.current.delete(tabValue);
              }}
              value={tabValue}
              className={tabTriggerClass}
            >
              {/* Decorative, and dropped below sm: with them, the Share
                  sheet's two labels leave its close X under 44px at 375px
                  and overflow at 360px (DV-3715's e2e). */}
              <Icon className="h-4 w-4 max-sm:hidden" aria-hidden />
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        {withDrawerClose && (
          <DrawerClose
            aria-label="Close"
            disabled={!dismissible}
            className="ml-auto inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </DrawerClose>
        )}
      </div>

      {/* Each tab sizes to its natural content, and the footer sits a
          consistent gap below the last content element — prioritized over a
          fixed modal height, since the visible empty gap above a
          bottom-pinned footer reads as a layout bug. Switching tabs animates
          between the two heights instead of jumping. */}
      <div
        data-testid="tabbed-modal-body"
        className="overflow-hidden transition-[height] duration-[280ms] ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none"
        style={contentHeight === undefined ? undefined : { height: contentHeight }}
      >
        <div ref={measureContent} className="p-4 sm:p-6">
          {children}
        </div>
      </div>
    </Tabs>
  );

  if (!isDesktop) {
    return (
      // vaul's own `dismissible`, not a refused close: a swipe past the close
      // point asks to close without putting the sheet back, so refusing it
      // would leave the sheet stuck part-way off-screen.
      <Drawer open={open} onOpenChange={onOpenChange} dismissible={dismissible}>
        {/* No description: the tabs and their content speak for themselves
            (as mobile-nav-drawer). */}
        <DrawerContent
          data-testid={sheetTestId}
          className="max-h-[92dvh]"
          aria-describedby={undefined}
          onCloseAutoFocus={handleCloseAutoFocus}
        >
          <DrawerTitle className="sr-only">{title}</DrawerTitle>
          <div className="overflow-y-auto">{tabbedBody(true)}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next || dismissible) onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-2xl p-0 gap-0 [&>button]:top-2"
        aria-describedby={undefined}
        onCloseAutoFocus={handleCloseAutoFocus}
        closeDisabled={!dismissible}
      >
        {/* `[&>button]:top-2` overrides the Dialog's default close-X `top-4`
            so its 32px box centers at 24px — vertically aligned with the
            h-12 tab strip's center. Scoped here only; other dialogs keep
            the corner-offset look the primitive ships with. */}
        <DialogTitle className="sr-only">{title}</DialogTitle>
        {tabbedBody(false)}
      </DialogContent>
    </Dialog>
  );
};

export default TabbedModal;
